import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAdvisedCriticalItemsHistoryCount } from "components/AdvisedCriticalHistory/components/AdvisedCriticalHistoryTable/hooks";
import {
  getCarNotesCount,
  getCustomerInfoCount,
  getPinnedItemsCount,
  getSnoozedItemsCount,
  hasDrivingTrend,
  hasTruckTyres,
  hasTyres
} from "modules/CarDetails/components";
import { getAppointmentHistoryCount, useCustomerInfoQuery, usePinItemsQuery, useSnoozeItemsQuery } from "modules/CarDetails/hooks";

import type { SectionMenu } from "components";
import type { Appointment } from "models";
import type { CarWithGroupedTruckTyres } from "modules/CarDetails/hooks";

export type CarDetailsScrollToSectionMenus = {
  details: SectionMenu;
  carNotes: SectionMenu;
  customerInfo: SectionMenu;
  snoozedItems: SectionMenu;
  pinnedItems: SectionMenu;
  advisedCriticalHistory: SectionMenu;
  appointmentHistory: SectionMenu;
  holdingAppointmentHistory: SectionMenu;
  tyres: SectionMenu;
  drivingTrend: SectionMenu;
};

type UseScrollToSectionMenusParams = {
  car?: CarWithGroupedTruckTyres;
  appointments?: Appointment[];
  holdingAppointments?: Appointment[];
};

export const useScrollToSectionMenus = ({ car, appointments, holdingAppointments }: UseScrollToSectionMenusParams) => {
  const { t, ready } = useTranslation();

  const latestAppointmentId = appointments?.at(0)?.id;
  const { data: customerInfo } = useCustomerInfoQuery(latestAppointmentId);
  const { data: snoozedItems = [] } = useSnoozeItemsQuery(car?.id);
  const { data: pinnedItems = [] } = usePinItemsQuery(car?.id);
  const advisedCriticalHistoryCount = useAdvisedCriticalItemsHistoryCount({ car_id: car?.id });

  return useMemo(
    (): CarDetailsScrollToSectionMenus => ({
      details: { id: "details", icon: "car mirrors", title: t("v8_details", "Details"), showEmpty: true },
      carNotes: { id: "carNotes", icon: "note sticky", title: t("v8_notes", "Notes"), count: getCarNotesCount(car), showEmpty: true },
      customerInfo: { id: "customerInfo", icon: "user tag", title: t("v8_customer_info", "Customer Info"), count: getCustomerInfoCount(customerInfo) },
      snoozedItems: { id: "snoozedItems", icon: "clock", title: t("v8_snoozed_items", "Snoozed Items"), count: getSnoozedItemsCount(snoozedItems) },
      pinnedItems: { id: "pinnedItems", icon: "bars", title: t("v8_pin_items", "Pin Items"), count: getPinnedItemsCount(pinnedItems) },
      advisedCriticalHistory: {
        id: "advisedCriticalHistory",
        icon: "clock rotate left",
        title: t("v8_advised_critical_items_history", "Advised and critical items history"),
        count: advisedCriticalHistoryCount
      },
      appointmentHistory: {
        id: "appointmentHistory",
        icon: "calendar clock",
        title: t("v8_appointment_history", "Appointment History"),
        count: getAppointmentHistoryCount(appointments)
      },
      holdingAppointmentHistory: {
        id: "holdingAppointmentHistory",
        icon: "calendar clock",
        title: t("v8_holding_appointment_history", "Holding Appointment History"),
        count: getAppointmentHistoryCount(holdingAppointments)
      },
      tyres: { id: "tyres", icon: "tire", title: t("v8_tyres", "Tyres"), showEmpty: hasTyres(car) || hasTruckTyres(car) },
      drivingTrend: { id: "drivingTrend", icon: "chart simple", title: t("v8_driving_trend", "Driving Trend"), showEmpty: hasDrivingTrend(appointments) }
    }),
    [ready, car, customerInfo, snoozedItems, pinnedItems, advisedCriticalHistoryCount, appointments, holdingAppointments]
  );
};
